import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import { useForm, FormProvider, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { BoldFormTitle } from '../styles/Text'
import { FlexEndContainer, FlexColumn, LightBGContainer, PaddedForm, StyledDialog, StyledDialogContent } from '../styles/Containers'
import Button, { SecondaryButton } from '../common/Button'
import { ErrorMessage, WarningMessage } from '../common/UserMessage'
import OwnerForm from '../pirForms/OwnerForm'
import ManualStrata from '../pirForms/ManualStrata'
import pirTerminology from '../../util/pirTerminology'
import RequestorForm from '../pirForms/RequestorForm'
import AdditionalRecipients from '../pirForms/AdditionalRecipients'
import { pirEditSchema } from '../../schemas/pirForm'
import { formatPIRForForm } from '../../util/pirFormatting'
import axios from '../../dataClient/dataClient'
import { postUploadPIRFile } from '../../dataClient/apiCalls'

const FormContainer = styled(FlexColumn)`
  gap: 15px;
`

const SectionContainer = styled(LightBGContainer)`
  padding: 20px;
`

const REGENERATED_DOCUMENT_COPY =
  'Changing the above data will void existing digital signatures and the signing document will be regenerated with the updated values. ' +
  'Individuals will need to sign the updated document.'
const NEW_DOCUMENT_COPY = 'This will generate a new signature document that requires all parties to re-sign.'

function EditPirDetailsDialog({ open, pir, onClose = () => {}, onSuccess = () => {} }) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const formattedPir = formatPIRForForm(pir).pir
  const methods = useForm({
    resolver: yupResolver(pirEditSchema),
    defaultValues: {
      ...formattedPir,
      strataState: pir.strataInformation ? 'manualStrata' : 'manualNoStrata'
    },
    context: {
      originalOwnerSignatureSource: pir.ownerInformation?.signatureSource,
      originalOwnerFirstName: formattedPir.ownerInformation.firstName,
      originalOwnerLastName: formattedPir.ownerInformation.lastName
    }
  })
  const { handleSubmit, reset, control } = methods
  const strataState = useWatch({ control, name: 'strataState' })
  const signatureSource = useWatch({ control, name: 'ownerInformation.signatureSource' })
  const originalStrataState = pir.strataInformation ? 'manualStrata' : 'manualNoStrata'
  const originalSignatureSource = (pir.ownerInformation?.signatureSource || '').toLowerCase()
  const createsNewDocument = strataState !== originalStrataState || signatureSource !== originalSignatureSource

  const onError = (errors, e) => console.error('Error', errors, e)

  useEffect(() => {
    if (open) {
      reset({
        ...formatPIRForForm(pir).pir,
        strataState: pir.strataInformation ? 'manualStrata' : 'manualNoStrata'
      })
      setError(false)
      setIsLoading(false)
    }
  }, [open, pir, reset])

  const handleClose = () => {
    setError(false)
    setIsLoading(false)
    onClose()
  }

  async function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    const { ownerInformation, requestorInformation, strataInformation, strataState, additionalRecipients = [] } = data
    const body = {
      strataState,
      ownerInformation: {
        firstName: ownerInformation.firstName,
        lastName: ownerInformation.lastName,
        address: ownerInformation.address,
        phone: ownerInformation.phone,
        email: ownerInformation.email,
        companyName: ownerInformation.ownedBy === 'company' ? ownerInformation.companyName : '',
        signatureSource: ownerInformation.signatureSource.toUpperCase()
      },
      requestorInformation: {
        firstName: requestorInformation.firstName,
        lastName: requestorInformation.lastName,
        address: requestorInformation.address,
        phone: requestorInformation.phone,
        email: requestorInformation.email,
        additionalName: requestorInformation.additionalName
      },
      strataInformation: null,
      additionalRecipients: additionalRecipients.map(({ email }) => ({ email }))
    }

    if (strataState === 'manualStrata') {
      body.strataInformation = {
        role: strataInformation.role,
        strataNumber: strataInformation.strataNumber,
        firstName: strataInformation.firstName,
        lastName: strataInformation.lastName,
        address: strataInformation.address,
        phone: strataInformation.phone,
        email: strataInformation.email,
        additionalName: strataInformation.additionalName
      }
    }

    const signatureFile = ownerInformation.signatureFile
    const hasSignatureFile = signatureFile instanceof File && signatureFile.size > 0

    axios
      .patch(`/pir/${pir.id}/request`, body)
      .then(() => (hasSignatureFile ? postUploadPIRFile(pir.id, signatureFile, true, 'owner') : Promise.resolve()))
      .then(() => {
        handleClose()
        onSuccess()
      })
      .catch(e => {
        console.error('Failed updating PIR request details', e)
        setError(true)
        setIsLoading(false)
      })
  }

  return (
    <StyledDialog fullWidth maxWidth="md" open={open} onClose={handleClose} keepMounted={false}>
      <StyledDialogContent>
        <FormProvider {...methods}>
          <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
            <BoldFormTitle>Edit {pirTerminology.getRequestShortName()} Details</BoldFormTitle>
            <FormContainer>
              <SectionContainer>
                <OwnerForm loading={isLoading} variant="staff" />
              </SectionContainer>
              <SectionContainer>
                <ManualStrata disabled={isLoading} variant="staff" />
              </SectionContainer>
              <SectionContainer>
                <RequestorForm loading={isLoading} variant="staff" />
                <AdditionalRecipients loading={isLoading} />
              </SectionContainer>
              <WarningMessage>{createsNewDocument ? NEW_DOCUMENT_COPY : REGENERATED_DOCUMENT_COPY}</WarningMessage>
              {error && <ErrorMessage>There was an error updating your details. Please try again.</ErrorMessage>}
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  Cancel
                </SecondaryButton>
                <Button type="submit" loading={isLoading}>
                  Save
                </Button>
              </FlexEndContainer>
            </FormContainer>
          </PaddedForm>
        </FormProvider>
      </StyledDialogContent>
    </StyledDialog>
  )
}

EditPirDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  pir: PropTypes.object.isRequired,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
}

export default EditPirDetailsDialog
